import React, { useState, useEffect } from 'react';
import useGameStore from '../store/gameStore';
import { BANKS, getLoyaltyLevel, getBankSeniorityMonths } from '../data/bankData';
import { useTranslation } from '../i18n';
import { localize } from '../data/i18nData';
import { EarlyRepayLoanModal } from '../components/game/EarlyRepayLoanModal';
import {
  Handshake, CreditCard, Wallet, PiggyBank, Shield,
} from 'lucide-react';

import { BankSummaryTiles } from '../components/banque/BankSummaryTiles';
import { ScoreRevealPopup } from '../components/banque/ScoreRevealPopup';
import { ConseillerTab } from '../components/banque/ConseillerTab';
import { CreditTab } from '../components/banque/CreditTab';
import { MyCreditsTab } from '../components/banque/MyCreditsTab';
import { PlacementsTab } from '../components/banque/PlacementsTab';
import { AssuranceTab } from '../components/banque/AssuranceTab';
import { VehicleFinancingCard } from '../components/banque/VehicleFinancingCard';

export default function Banque() {
  const { t } = useTranslation();
  const {
    treasury, reputation, vehicles, credits, monthlyRevenues, totalDays, monthlyHistory,
    bankRelations, pendingProposals, activePlacements,
    requestLoanProposals, acceptLoanProposal, dismissProposals,
    subscribePlacement, withdrawPlacement,
    creditScore, scoreRevealed, scoreRevealMessage, scoreRevealDismissed, dismissScoreReveal,
    checkScoreReveal, updateCreditScore,
    setInsuranceCoverage,
  } = useGameStore();

  useEffect(() => {
    if (!scoreRevealed && totalDays >= 90) {
      checkScoreReveal();
    } else if (scoreRevealed) {
      updateCreditScore();
    }
  }, [totalDays, scoreRevealed, checkScoreReveal, updateCreditScore]);

  const [selectedBank, setSelectedBank] = useState('bnb_mariba');
  const [tab, setTab] = useState('conseillers');
  const [loanAmount, setLoanAmount] = useState(15000);
  const [loanDuration, setLoanDuration] = useState(24);
  const [placementAmount, setPlacementAmount] = useState(10000);
  const [repayingLoan, setRepayingLoan] = useState(null);

  const totalDebt = credits.reduce((s, c) => s + (c.remainingPrincipal || 0), 0);
  const monthlyCredit = credits.reduce((s, c) => s + (c.monthlyPayment || 0), 0);
  const bank = localize('bank', BANKS[selectedBank]);
  const relation = bankRelations[selectedBank] || { loansRepaid: 0, totalBorrowed: 0, activeLoanCount: 0, loyaltyLevel: 0 };
  const seniorityMonths = getBankSeniorityMonths(relation, totalDays);
  const loyaltyLevel = getLoyaltyLevel(selectedBank, relation.loansRepaid, seniorityMonths || 0);
  const totalPlacementValue = activePlacements.reduce((s, p) => s + p.amount, 0);

  const TABS = [
    { id: 'conseillers', label: t('advisors_tab'),    icon: Handshake },
    { id: 'credit',      label: t('request_loan_tab'), icon: CreditCard },
    { id: 'mes_credits', label: t('my_loans_tab'),    icon: Wallet, badge: credits.length },
    { id: 'placements',  label: t('placements_label'), icon: PiggyBank },
    { id: 'assurance',   label: 'Assurance',           icon: Shield },
  ];

  return (
    <div className="space-y-4" data-testid="banque-page">
      <h2 className="text-2xl font-bold text-foreground">{t('bank_title')}</h2>

      {/* 13/07/2026 — dossier spécial crédit véhicule (arc confrère retraité) */}
      <VehicleFinancingCard />

      <BankSummaryTiles
        totalDebt={totalDebt}
        credits={credits}
        monthlyCredit={monthlyCredit}
        totalPlacementValue={totalPlacementValue}
        activePlacements={activePlacements}
        bank={bank}
        loyaltyLevel={loyaltyLevel}
        relation={relation}
        scoreRevealed={scoreRevealed}
        creditScore={creditScore}
      />

      {scoreRevealed && scoreRevealMessage && !scoreRevealDismissed && (
        <ScoreRevealPopup
          scoreRevealMessage={scoreRevealMessage}
          creditScore={creditScore}
          onDismiss={dismissScoreReveal}
        />
      )}

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {TABS.map(it => (
          <button
            key={it.id}
            onClick={() => setTab(it.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
              tab === it.id ? 'bg-accent text-white' : 'bg-ink-800 text-slate-400 border border-ink-600'
            }`}
            data-testid={`bank-tab-${it.id}`}
          >
            <it.icon className="w-3.5 h-3.5" /> {it.label}
            {it.badge > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-danger/100 text-white text-[9px] font-bold">{it.badge}</span>}
          </button>
        ))}
      </div>

      {tab === 'conseillers' && (
        <ConseillerTab
          selectedBank={selectedBank}
          setSelectedBank={setSelectedBank}
          bank={bank}
          bankRelations={bankRelations}
          relation={relation}
          loyaltyLevel={loyaltyLevel}
          reputation={reputation}
          vehicles={vehicles}
          monthlyRevenues={monthlyRevenues}
          monthlyHistory={monthlyHistory}
          treasury={treasury}
          totalDebt={totalDebt}
          totalDays={totalDays}
        />
      )}

      {tab === 'credit' && (
        <CreditTab
          pendingProposals={pendingProposals}
          loanAmount={loanAmount}
          setLoanAmount={setLoanAmount}
          loanDuration={loanDuration}
          setLoanDuration={setLoanDuration}
          reputation={reputation}
          vehicles={vehicles}
          monthlyRevenues={monthlyRevenues}
          monthlyHistory={monthlyHistory}
          totalDebt={totalDebt}
          totalDays={totalDays}
          requestLoanProposals={requestLoanProposals}
          acceptLoanProposal={acceptLoanProposal}
          dismissProposals={dismissProposals}
        />
      )}

      {tab === 'mes_credits' && (
        <MyCreditsTab credits={credits} setRepayingLoan={setRepayingLoan} />
      )}

      {tab === 'placements' && (
        <PlacementsTab
          activePlacements={activePlacements}
          bankRelations={bankRelations}
          treasury={treasury}
          placementAmount={placementAmount}
          setPlacementAmount={setPlacementAmount}
          subscribePlacement={subscribePlacement}
          withdrawPlacement={withdrawPlacement}
          totalDays={totalDays}
        />
      )}

      {tab === 'assurance' && (
        <AssuranceTab vehicles={vehicles} setInsuranceCoverage={setInsuranceCoverage} />
      )}

      {repayingLoan && (
        <EarlyRepayLoanModal
          loan={repayingLoan}
          onClose={() => setRepayingLoan(null)}
        />
      )}
    </div>
  );
}
